import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { ChartDataPoint, AggregatedData } from '../../utils/dataAggregation';
import { ChartType, ChartGranularity } from '../../utils/chartUtils';
import './AdaptiveProfitChart.css';

/**
 * Metric type that can be displayed on the chart
 */
export type ChartMetric = 'net_profit' | 'gross_profit' | 'trade_count';

/**
 * Props for the AdaptiveProfitChart component
 */
export interface AdaptiveProfitChartProps {
  /** Chart data points */
  data: (ChartDataPoint | AggregatedData)[];
  /** Chart granularity */
  granularity: ChartGranularity;
  /** Chart type */
  chartType: ChartType;
  /** Metric to display */
  metric: ChartMetric;
  /** Whether to show cumulative line */
  showCumulative?: boolean;
  /** Click handler for data points */
  onDataPointClick?: (dataPoint: ChartDataPoint | AggregatedData) => void;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** Empty state message */
  emptyMessage?: string;
}

/**
 * AdaptiveProfitChart Component
 * 
 * Renders an adaptive chart that automatically adjusts its type (bar/line)
 * and granularity based on the selected date range.
 */
const AdaptiveProfitChart: React.FC<AdaptiveProfitChartProps> = ({
  data,
  chartType,
  metric,
  showCumulative = false,
  onDataPointClick,
  loading = false,
  error = null,
  emptyMessage = 'No data available for this period'
}) => {
  
  // Calculate cumulative data if enabled
  const chartData = useMemo(() => {
    if (!showCumulative) return data;
    
    let cumulative = 0;
    return data.map(point => {
      cumulative += point.net_profit;
      return {
        ...point,
        cumulative_profit: cumulative
      };
    });
  }, [data, showCumulative]);

  // Get the metric value from a data point
  const getMetricValue = (point: any): number => {
    return point[metric] || 0;
  };

  // Get color for a value
  const getColor = (value: number): string => {
    if (metric === 'trade_count') {
      return '#8a8ced'; // Purple accent for trade count
    }
    return value >= 0 ? '#10b981' : '#ef4444'; // Green/Red for profit
  };

  // Format Y-axis label
  const formatYAxis = (value: number): string => {
    if (metric === 'trade_count') {
      return value.toString();
    }
    if (Math.abs(value) >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Format tooltip
  const formatTooltipValue = (value: number): string => {
    if (metric === 'trade_count') {
      return value.toString();
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;
    const isAggregated = 'period_label' in data;

    return (
      <div className="chart-tooltip">
        <div className="tooltip-header">
          {isAggregated ? data.period_label : data.date}
        </div>
        <div className="tooltip-divider" />
        <div className="tooltip-content">
          <div className="tooltip-row">
            <span className="tooltip-label">Net Profit:</span>
            <span className="tooltip-value">{formatTooltipValue(data.net_profit)}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Gross Profit:</span>
            <span className="tooltip-value">{formatTooltipValue(data.gross_profit)}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Fees:</span>
            <span className="tooltip-value">{formatTooltipValue(data.fees_paid)}</span>
          </div>
          <div className="tooltip-row">
            <span className="tooltip-label">Trade Count:</span>
            <span className="tooltip-value">{data.trade_count}</span>
          </div>
          {isAggregated && data.trading_days > 0 && (
            <>
              <div className="tooltip-row">
                <span className="tooltip-label">Trading Days:</span>
                <span className="tooltip-value">{data.trading_days}</span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Avg/Day:</span>
                <span className="tooltip-value">
                  {formatTooltipValue(data.net_profit / data.trading_days)}
                </span>
              </div>
            </>
          )}
          {!isAggregated && data.trade_count > 0 && (
            <div className="tooltip-row">
              <span className="tooltip-label">Avg/Trade:</span>
              <span className="tooltip-value">
                {formatTooltipValue(data.net_profit / data.trade_count)}
              </span>
            </div>
          )}
          {showCumulative && 'cumulative_profit' in data && (
            <>
              <div className="tooltip-divider" />
              <div className="tooltip-row">
                <span className="tooltip-label">Cumulative:</span>
                <span className="tooltip-value tooltip-cumulative">
                  {formatTooltipValue(data.cumulative_profit)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Handle bar/point click
  const handleClick = (data: any) => {
    if (onDataPointClick && data) {
      onDataPointClick(data);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="chart-loading">
        <div className="loading-spinner"></div>
        <p>Loading chart data...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="chart-error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="chart-empty">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Common chart props
  const commonProps = {
    data: chartData,
    margin: { top: 10, right: 10, left: 0, bottom: 0 }
  };

  // X-axis props
  const xAxisProps = {
    dataKey: 'date',
    stroke: '#6b7280',
    tick: { fill: '#9ca3af', fontSize: 11 },
    axisLine: { stroke: '#3d4557' }
  };

  // Y-axis props
  const yAxisProps = {
    stroke: '#6b7280',
    tick: { fill: '#9ca3af', fontSize: 11 },
    axisLine: { stroke: '#3d4557' },
    tickFormatter: formatYAxis
  };

  // Grid props
  const gridProps = {
    strokeDasharray: '3 3',
    stroke: '#3d4557',
    vertical: false
  };

  // Render bar chart
  if (chartType === 'bar') {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart {...commonProps}>
          <CartesianGrid {...gridProps} />
          <XAxis {...xAxisProps} />
          <YAxis {...yAxisProps} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }} />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
          <Bar
            dataKey={metric}
            onClick={handleClick}
            cursor="pointer"
            radius={[2, 2, 0, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(getMetricValue(entry))} />
            ))}
          </Bar>
          {showCumulative && (
            <Line
              type="monotone"
              dataKey="cumulative_profit"
              stroke="#8a8ced"
              strokeWidth={2}
              dot={{ fill: '#8a8ced', r: 3 }}
              activeDot={{ r: 5 }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Render line chart
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart {...commonProps}>
        <CartesianGrid {...gridProps} />
        <XAxis {...xAxisProps} />
        <YAxis {...yAxisProps} />
        <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(139, 92, 246, 0.3)' }} />
        <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
        <Line
          type="monotone"
          dataKey={metric}
          stroke={metric === 'trade_count' ? '#8a8ced' : '#10b981'}
          strokeWidth={2}
          dot={{ r: 4 }}
          activeDot={{ r: 6, onClick: handleClick, cursor: 'pointer' }}
        />
        {showCumulative && (
          <Line
            type="monotone"
            dataKey="cumulative_profit"
            stroke="#8a8ced"
            strokeWidth={2}
            dot={{ fill: '#8a8ced', r: 3 }}
            activeDot={{ r: 5 }}
            strokeDasharray="5 5"
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default AdaptiveProfitChart;
